// Publish, and the versions to roll back to — the page screen's half of
// api/code/src/model/page/publish.
//
// Two components out of one query, so that the button in the action row and
// the block below the fields cannot disagree about whether there is anything
// to publish. `PageQL.versions` is the one document that selects a page's
// kept versions, and the mutations answer in the same shape, so a publish or
// a rollback updates both from the mutation's own result — no refetch and no
// eviction. The server decides `changed`; this screen only shows it.
//
// A ROLLBACK ASKS TWICE. It is the one control here that changes what a
// visitor sees without a screen of its own in front of it — Remove has one,
// Publish is the thing you came here to do — so the button becomes a
// confirmation rather than acting. Inline rather than a dialog, because a
// browser dialog is invisible to the suites and to a screen reader alike.
import React, { useState } from "react";
import { Button, Table } from "reactstrap";
import { useMutation, useQuery } from "@apollo/client";
import * as Notify from "@repoflow/form-schema/notify";
import * as PageQL from "queries/page";

export const PAGE_PATH = "viewer.account.sites.get.pages.get";

// The error the model raises, in a sentence.
export const publishError = (error) => {
  const text = (error && error.toString()) || "";

  if (text.includes("page.publish.unchanged")) {
    return "Nothing has changed since this page was last published.";
  }

  if (text.includes("page.rollback.notfound")) {
    return "That version is no longer kept. Reload the page.";
  }

  if (text.includes("page.rollback.current")) {
    return "That version is the one being served.";
  }

  return text;
};

// What the screen says about what visitors see, from the three answers the
// API gives — see PagePublished in the api schema for what each combination
// means.
export const publishSentence = (page) => {
  if (!page) {
    return "";
  }

  if (!page.served) {
    return "Not published. Visitors get nothing at this page's route until it is.";
  }

  if (!page.published) {
    return (
      "Serving the draft, as this page did before publishing existed. " +
      "Publish it once to start keeping versions to roll back to."
    );
  }

  return page.changed
    ? "The draft has changes that are not published."
    : "Published. Visitors see the draft as it is.";
};

const when = (value) => (value ? new Date(value).toLocaleString() : "");

const readPage = (data) => {
  try {
    return data.viewer.account.sites.get.pages.get;
  } catch (e) {
    return null;
  }
};

// The page with its versions. `cache-and-network`: the entity is already in
// the cache from the route's own query, so the button lights from that at
// once, and the versions arrive with the network answer.
export const usePublishState = (siteId, pageId) => {
  const { data, loading } = useQuery(PageQL.versions, {
    variables: { siteId, pageId },
    fetchPolicy: "cache-and-network",
  });

  return { page: readPage(data), loading };
};

export const PublishButton = ({ siteId, pageId }) => {
  const { page } = usePublishState(siteId, pageId);

  const [run, { loading }] = useMutation(PageQL.publish, {
    variables: { siteId, pageId },
    onCompleted: () => Notify.success("Page published"),
    onError: (error) => Notify.error(publishError(error)),
  });

  return (
    <Button
      color="success"
      disabled={loading || !page || !page.changed}
      title={page && !page.changed ? "Nothing has changed since the last publish" : undefined}
      onClick={() => run()}
    >
      {loading ? "Publishing…" : "Publish"}
    </Button>
  );
};

export const PublishPanel = ({ siteId, pageId }) => {
  const { page } = usePublishState(siteId, pageId);

  // Which version's Roll back is waiting to be confirmed, if any. One at a
  // time: opening a second closes the first.
  const [confirming, setConfirming] = useState(null);

  const [rollback, { loading }] = useMutation(PageQL.rollback, {
    onCompleted: () => {
      setConfirming(null);
      Notify.success("Rolled back. The draft is unchanged.");
    },
    onError: (error) => {
      setConfirming(null);
      Notify.error(publishError(error));
    },
  });

  if (!page) {
    return null;
  }

  const versions = page.versions || [];

  return (
    <>
      <h6 className="text-muted mb-3">Publishing</h6>
      <p data-testid="publish-state">
        {publishSentence(page)}
        {page.published ? (
          <>
            {" "}
            Live since {when(page.published.publishedAt)} on{" "}
            <code>{page.published.route || "/"}</code>.
          </>
        ) : null}
      </p>
      {versions.length > 0 ? (
        <Table size="sm" aria-label="Versions">
          <thead>
            <tr>
              <th>Published</th>
              <th>Title</th>
              <th>Route</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {versions.map((version) => (
              <tr key={version.versionId}>
                <td>{when(version.publishedAt)}</td>
                <td>{version.title}</td>
                <td>
                  <code>{version.route || "/"}</code>
                </td>
                <td className="text-right">
                  {version.current ? (
                    <span className="badge badge-success">Live</span>
                  ) : confirming === version.versionId ? (
                    <>
                      <Button
                        size="sm"
                        color="warning"
                        disabled={loading}
                        onClick={() =>
                          rollback({
                            variables: { siteId, pageId, input: { versionId: version.versionId } },
                          })
                        }
                      >
                        {loading ? "Rolling back…" : "Confirm roll back"}
                      </Button>{" "}
                      <Button size="sm" color="link" disabled={loading} onClick={() => setConfirming(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      color="secondary"
                      onClick={() => setConfirming(version.versionId)}
                    >
                      Roll back
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </>
  );
};
